import { LitElement, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { VesselPositionModel } from '../../../src/models/vessel-position-model';
import { GetFormattedDate } from '../../../src/utils/helpers';
import { showCoachMark } from '../../../src/utils/coach-marks-utils';
import '../../voy-lat-long-input/src/voy-lat-long-input';
import { VoyLatLongInput } from '../../voy-lat-long-input/src/voy-lat-long-input';

import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { OnlineStatusController } from '../../../src/utils/online-status-controller';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('voy-vessel-position-alert')
export class VoyVesselPositionAlert extends LitElement {
    private onlineStatus = new OnlineStatusController(this);
    constructor() {
        super();

        this._onPositionUpdated = this._onPositionUpdated.bind(this);
    }

    static get styles() {
        return [
            css`
                :host {
                    display: block;
                }

                :host([hidden]) {
                    display: none;
                }

                .alert {
                    width: 100%;
                    padding: 15px;

                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    font-size: 14px;
                    font-family: MaerskText;
                    color: var(--neutrals-grey-02, black);
                    background: var(--voy-alert-message-background-color, white);
                    border: 1px solid var(--voyage-error-red, red);
                    box-sizing: border-box;
                    border-radius: 3px;
                }

                .alert.fresh {
                    background: #d4edda;
                    border: 1px solid var(--voyage-positive-green, green);
                }

                .icon {
                    color: var(--voyage-error-red, red);
                    margin-right: 10px;
                }

                .alert.fresh .icon {
                    color: var(--voyage-positive-green, green);
                }

                .icon > svg {
                    height: 18px;
                    display: block;
                    fill: currentColor;
                }

                .message {
                    display: flex;
                    align-items: center;
                    color: var(--voyage-error-red, red);
                    font-size: 16px;
                    margin-bottom: 10px;
                }

                .alert.fresh .message {
                    color: var(--voyage-positive-green, green);
                }

                button {
                    height: 32px;
                    min-width: 80px;
                    font-size: 14px;
                    font-family: MaerskText;
                    padding: 0px 12px 0px 12px;
                    border: none;
                    outline: none;
                    cursor: pointer;
                    border-radius: 3px;
                    background: var(--primary-blue-05, blue);
                    margin-right: 20px;
                    color: var(--neutrals-grey-10, white);
                }

                button:disabled {
                    background: var(--neutrals-grey-06, grey);
                    cursor: auto;
                }

                #update-position-btn.highlight, #latitude-input.highlight, #longitude-input.highlight {
                    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.9), 0 0 0 5px rgba(0, 115, 171, 0.9);
                    border-radius: 4px;
                    transition: box-shadow 0.3s ease;
                }

                .inputRow {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }

                .inputRow > * {
                    margin-right: 5px;
                }

                .freshness-info {
                    margin-bottom: 10px;
                    font-size: 14px;
                    color: var(--voyage-positive-green, green);
                    font-weight: 500;
                }
            `
        ];
    }

    render() {
        const source = isVesselOnCloud ? 'AIS' : 'GPS';
        const disableUpdate = !this._inputValid || (isVesselOnCloud && !this.onlineStatus.isOnline);
        let alertMessage = '';
        if (VesselPositionModel!.usingManualPositionOverride) {
            alertMessage = `You have currently disabled ${source} position and are relying on manual input`;
            if (this._manualPositionFreshness) {
                alertMessage += ` (${this._manualPositionFreshness})`;
            }
        }
        else if (this._malfunctioningGPS) {
            alertMessage = `Integrated ${source} source not working: ` + this._malfunctionDetails;
        }

        const alertClass = this._isManualPositionFresh ? 'alert fresh' : 'alert';
        return html`
        <div class="${alertClass}">
            <div class="message">
                <div class="icon">${SvgIcons.lightExclamationTriangle}</div>
                <div>${alertMessage}</div>
            </div>
            
            ${this._isManualPositionFresh && this._manualPositionFreshness ? html`
                <div class="freshness-info">Manual position freshness: ${this._manualPositionFreshness}</div>
            ` : ''}
            
            <div>${this._positionUpdate}Update current position below:</div>

            <div class="inputRow">
                <div>Latitude</div>
                <voy-lat-long-input id="latitude-input" .value=${this._manualLat} @input=${this._onInput}></voy-lat-long-input>
                <div>Longitude</div>
                <voy-lat-long-input id="longitude-input" long .value=${this._manualLong} @input=${this._onInput}></voy-lat-long-input>
                <button id="update-position-btn" @click=${this._setPosition} .disabled=${disableUpdate}>Update Test</button>
            </div>
        </div>
        `;
    }

    private _coachMarks: ReturnType<typeof showCoachMark> | null = null;
    // Tracks whether we have already attempted to show the coach mark this session,
    // preventing repeated attempts on every position-updated event.
    private _coachMarkAttempted: boolean = false;

    firstUpdated() {
        VesselPositionModel!.addEventListener("position-updated", this._onPositionUpdated);
        this._onPositionUpdated();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VesselPositionModel!.removeEventListener("position-updated", this._onPositionUpdated);
        // Dismiss the coach mark if the component is removed from the DOM.
        this._coachMarks?.hide();
        this._coachMarks = null;
    }

    @property({ attribute: false }) private _malfunctioningGPS: boolean = false;
    @property({ attribute: false }) private _malfunctionDetails: string = "";
    @property({ attribute: false }) private _manualLat: number | null = null;
    @property({ attribute: false }) private _manualLong: number | null = null;
    @property({ attribute: false }) private _inputValid: boolean = false;
    @property({ attribute: false }) private _positionUpdate: string = "";
    @property({ attribute: false }) private _isManualPositionFresh: boolean = false;
    @property({ attribute: false }) private _manualPositionFreshness: string = "";

    private _onPositionUpdated() {
        // Check if any non-manual source is outdated
        const hasOutdatedNonManualSource = this._checkIfPositionOutdated();

        if (VesselPositionModel!.usingManualPositionOverride || VesselPositionModel!.malfunctioningGPS || hasOutdatedNonManualSource) {
            this.removeAttribute("hidden");
        }
        else {
            this.setAttribute("hidden", "")
        }

        // --- Temp coachmark logic ---
        // Show the coach mark the first time this component becomes visible.
        // We wait for updateComplete so all reactive property changes from this
        // call have re-rendered (and the hidden attribute is settled) before the
        // library checks element visibility. The localStorage flag ensures it
        // never shows again once the user has acknowledged it.
        // if (!this._coachMarkAttempted && !this.hasAttribute('hidden') &&
        //         localStorage.getItem('coachmark-update-position-seen') !== 'true') {
        //     this._coachMarkAttempted = true;
        //     this.updateComplete.then(() => {
        //         this._coachMarks = showCoachMark({
        //             shadowRoot: this.shadowRoot!,
        //             debug: true,
        //             theme: 'dark',
        //             items: [
        //                 {
        //                 title: 'Latitude',
        //                 text: 'You can enter the latitude here.',
        //                 target: '#latitude-input',
        //             },
        //             {
        //                 title: 'Longitude',
        //                 text: 'You can enter the longitude here.',
        //                 target: '#longitude-input',
        //             },
        //             {
        //                 title: 'Update vessel position',
        //                 text: 'Enter the latitude and longitude, then click Update to manually set the vessel position.',
        //                 target: '#update-position-btn',
        //                 seen: () => {
        //                     // localStorage.setItem('coachmark-update-position-seen', 'true');
        //                     this._coachMarks = null;
        //                 },
        //             }],
        //         });
        //     });
        // }

        this._malfunctioningGPS = VesselPositionModel!.malfunctioningGPS || hasOutdatedNonManualSource;
        if (this._malfunctioningGPS) {
            const ageText = this._getPositionAge();
            this._malfunctionDetails = ageText ? `Position is outdated${ageText}` : "No data available";
        }

        const position = VesselPositionModel!.position;
        if (position) {
            this._manualLat = position.latitude;
            this._manualLong = position.longitude;

            if (position.source === "manual") {
                this._positionUpdate = `Last position update is from ${GetFormattedDate(position.timestamp)} (UTC).`;
                
                // Calculate manual position freshness
                const manualAgeInfo = this._getManualPositionAge();
                this._isManualPositionFresh = manualAgeInfo.isFresh;
                this._manualPositionFreshness = manualAgeInfo.ageText;
            }
            else {
                this._positionUpdate = "";
                this._isManualPositionFresh = false;
                this._manualPositionFreshness = "";
            }
        }
        else {
            this._positionUpdate = "";
            this._isManualPositionFresh = false;
            this._manualPositionFreshness = "";
        }

        this._checkValidState();
    }

    private _onInput(evt: InputEvent) {
        const target = <VoyLatLongInput>evt.target;
        if (target.long) {
            this._manualLong = target.value;
        }
        else {
            this._manualLat = target.value;
        }
        this._checkValidState();
    }

    private _checkValidState() {
        const inputs = <NodeListOf<VoyLatLongInput>>this.shadowRoot!.querySelectorAll('voy-lat-long-input');
        this._inputValid = !inputs[0].invalid && !inputs[1].invalid;
    }

    private _checkIfPositionOutdated(): boolean {
        const position = VesselPositionModel!.position;
        if (!position || !position.dataSources) return false;

        const outOfDateTime = 1000 * 60 * 30; // 30 minutes grace period
        let latestNonManualTimestamp: string | null = null;

        // Find the latest timestamp from all non-manual sources
        for (const [sourceName, sourceData] of Object.entries(position.dataSources)) {
            if (sourceName.toLowerCase() !== 'manual' && sourceData.timestamp) {
                if (!latestNonManualTimestamp || sourceData.timestamp > latestNonManualTimestamp) {
                    latestNonManualTimestamp = sourceData.timestamp;
                }
            }
        }

        // If we found a non-manual source, check if it's outdated
        if (latestNonManualTimestamp) {
            const positionTime = new Date(latestNonManualTimestamp).getTime();
            const isOutdated = positionTime + outOfDateTime < Date.now();
            return isOutdated;
        }

        // No non-manual sources found
        return true;
    }

    private _getPositionAge(): string {
        const position = VesselPositionModel!.position;
        if (!position) return '';

        try {
            let latestTimestamp: string | null = null;

            // Check dataSources for non-manual sources
            if (position.dataSources) {
                for (const [sourceName, sourceData] of Object.entries(position.dataSources)) {
                    if (sourceName.toLowerCase() !== 'manual' && sourceData.timestamp) {
                        if (!latestTimestamp || sourceData.timestamp > latestTimestamp) {
                            latestTimestamp = sourceData.timestamp;
                        }
                    }
                }
            }

            // Fallback to main timestamp if no non-manual sources found
            if (!latestTimestamp && position.timestamp) {
                latestTimestamp = position.timestamp;
            }

            if (!latestTimestamp) return '';

            const positionTime = new Date(latestTimestamp).getTime();
            const now = Date.now();
            const ageMs = now - positionTime;

            if (ageMs < 0) return ''; // Future timestamp, ignore

            const ageMinutes = Math.floor(ageMs / 60000);
            const ageHours = Math.floor(ageMinutes / 60);
            const ageDays = Math.floor(ageHours / 24);

            if (ageDays > 0) {
                return ` (${ageDays} day${ageDays > 1 ? 's' : ''} old)`;
            } else if (ageHours > 0) {
                return ` (${ageHours} hour${ageHours > 1 ? 's' : ''} old)`;
            } else if (ageMinutes > 0) {
                return ` (${ageMinutes} minute${ageMinutes > 1 ? 's' : ''} old)`;
            }
            return ' (less than 1 minute old)';
        } catch (e) {
            return '';
        }
    }

    private _getManualPositionAge(): { isFresh: boolean; ageText: string } {
        const position = VesselPositionModel!.position;
        if (!position || position.source !== 'manual' || !position.timestamp) {
            return { isFresh: false, ageText: '' };
        }

        try {
            const positionTime = new Date(position.timestamp).getTime();
            const now = Date.now();
            const ageMs = now - positionTime;

            if (ageMs < 0) return { isFresh: false, ageText: '' }; // Future timestamp, ignore

            const ageMinutes = Math.floor(ageMs / 60000);
            const ageHours = Math.floor(ageMinutes / 60);
            const ageDays = Math.floor(ageHours / 24);

            const isFresh = ageMinutes < 30;
            let ageText = '';

            if (ageDays > 0) {
                ageText = `${ageDays} day${ageDays > 1 ? 's' : ''} old`;
            } else if (ageHours > 0) {
                ageText = `${ageHours} hour${ageHours > 1 ? 's' : ''} old`;
            } else if (ageMinutes > 0) {
                ageText = `${ageMinutes} minute${ageMinutes > 1 ? 's' : ''} old`;
            } else {
                ageText = 'less than 1 minute old';
            }

            return { isFresh, ageText };
        } catch (e) {
            return { isFresh: false, ageText: '' };
        }
    }

    private _setPosition() {
        if (this._manualLat !== null && this._manualLong !== null) {
            VesselPositionModel!.setManualPosition([this._manualLong, this._manualLat]);
        }
    }
};
